import {
  Divider,
  Flex,
  List,
  ListItem,
  Popover,
  PopoverContent,
  PopoverProps,
  PopoverTrigger,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useCombobox } from "downshift";
import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import {
  filterGroups,
  toGroupList,
  type Group,
  type Item,
} from "src/types/groupedItems";
import { SearchInput } from "../Inputs/SearchInput";
import { HighlightedText } from "../Text/HighlightedText";

type Props<ITEM extends Item> = {
  items: ITEM[] | Group<ITEM>[];
  title?: string;
  popoverTrigger: React.ReactNode;
  searchPlaceholder?: string;
  onSearch?: (searchTerm: string) => void | Promise<void>;
  onItemSelected: (item: ITEM) => void | Promise<void>;
  renderItem?: (item: ITEM, keyword: string) => React.ReactNode;
  onOpen?: () => void;
  popoverProps?: Omit<
    PopoverProps,
    "isOpen" | "onOpen" | "onClose" | "children" | "initialFocusRef"
  >;
};
export function SearchableMenuPopover<ITEM extends Item>({
  items,
  title,
  popoverTrigger,
  searchPlaceholder,
  renderItem,
  onSearch,
  onItemSelected,
  popoverProps,
}: Props<ITEM>) {
  const inputRef = useRef(null);
  const [keyword, setKeyword] = useState("");
  const { onClose, onOpen, isOpen } = useDisclosure();

  const groups = useMemo(() => toGroupList(items), [items]);

  const filteredGroups = useMemo(() => {
    if (!isOpen) {
      return [];
    }
    return filterGroups(groups, keyword);
  }, [isOpen, groups, keyword]);

  const filteredItems = useMemo(
    () => filteredGroups.flatMap((group) => group.items),
    [filteredGroups]
  );

  const combobox = useCombobox({
    items: filteredItems,
    isOpen,
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) onItemSelected(selectedItem);
      combobox.reset();
      onClose();
      setTimeout(() => {
        reset();
      }, 100);
    },
  });

  const reset = () => {
    setKeyword("");
  };

  const inputProps = useMemo(() => {
    const { onChange, value, ...props } = combobox.getInputProps({
      ref: inputRef,
    });
    return props;
  }, [combobox]);

  const onSearchHandler = useCallback(
    async (searchTerm: string) => {
      setKeyword(searchTerm);
      if (onSearch) {
        await onSearch(searchTerm);
      }
    },
    [onSearch]
  );

  const memoizedRenderItem = useCallback(
    (item: ITEM, keyword: string) => {
      if (renderItem) {
        return renderItem(item, keyword);
      }
      return <HighlightedText invert text={item.label} keyword={keyword} />;
    },
    [renderItem]
  );

  const listItemsNode = useMemo(() => {
    if (!isOpen) return null;

    let index = 0;
    return filteredGroups.map((group) => {
      const itemsInGroup = group.items.map((item) => {
        const itemIndex = index;
        index += 1;
        return (
          <ListItem
            key={item.key}
            width="100%"
            cursor="pointer"
            bg={
              combobox.highlightedIndex === itemIndex ? "gray.200" : undefined
            }
            paddingX="4"
            paddingY="2"
            {...combobox.getItemProps({ item, index: itemIndex })}
          >
            {memoizedRenderItem(item, keyword)}
          </ListItem>
        );
      });

      return (
        <Fragment key={group.originalIndex}>
          {group.group !== null ? (
            <Flex fontWeight="bold" width="100%" paddingX="4" paddingY="2">
              {group.group}
            </Flex>
          ) : null}
          {itemsInGroup}
          <Divider />
        </Fragment>
      );
    });
  }, [isOpen, filteredGroups, combobox, memoizedRenderItem, keyword]);

  return (
    <Popover
      placement="bottom-start"
      {...popoverProps}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={() => {
        onClose();
        setTimeout(() => {
          reset();
        }, 100);
      }}
      initialFocusRef={inputRef}
    >
      <PopoverTrigger>{popoverTrigger}</PopoverTrigger>
      <PopoverContent zIndex="1000">
        <Flex direction="column" padding="4" gap="2">
          {title ? (
            <Text fontSize="lg" fontWeight="bold">
              {title}
            </Text>
          ) : null}
          <SearchInput
            variant="leftIcon"
            placeholder={searchPlaceholder}
            onSearchChange={onSearchHandler}
            inputProps={inputProps}
            ref={inputProps.ref}
            value={keyword}
          />
        </Flex>
        <Divider />
        <List
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          maxHeight="30rem"
          overflowY="auto"
          {...combobox.getMenuProps()}
        >
          {listItemsNode}
        </List>
      </PopoverContent>
    </Popover>
  );
}
